import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, ShieldAlert } from "lucide-react";
import Seo from "@/components/common/Seo";
import { sharedApi } from "@/lib/api";

/**
 * Iter-626 §3 — Admin "Purchased points" mirror (staff with rewards or
 * finance role only; everyone else gets the not-authorised card).
 *   GET  /points/purchase/admin/list?limit=200
 *   GET  /points/admin/purchased-points/pnl?months=12
 *   POST /points/purchase/admin/{id}/refund {reason, refund_stripe:true}
 * Hidden route (/admin/points-purchases) — the web app has no admin nav.
 */

const nzd = (v) => `$${Number(v || 0).toFixed(2)}`;

/** Iter 637 — suspicious-transaction alerts (payout holds + resolve).
 *  GET /admin/engagement/risk-alerts?status=open — owner/manager/finance
 *  only; hidden on 403 / older backends. Clear releases the payout hold. */
function RiskAlertRow({ a }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const resolveMut = useMutation({
    mutationFn: async (action) =>
      (
        await sharedApi.post(`/admin/engagement/risk-alerts/${a.id}/resolve`, {
          action,
          note: note.trim() || undefined,
        })
      ).data,
    onSuccess: (_r, action) => {
      toast.success(action === "clear" ? "Cleared — payout released." : `Resolved (${action}).`);
      qc.invalidateQueries({ queryKey: ["admin-risk-alerts"] });
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't resolve the alert");
    },
  });
  const open = a.status === "open";
  return (
    <li className="py-2.5 border-b border-slate-100 last:border-0" data-testid={`risk-alert-${a.id}`}>
      <div className="text-xs text-slate-700">
        <span className="font-extrabold text-slate-900">
          {open ? "🚨" : "✅"} {a.kind}
        </span>{" "}
        — {a.summary || `$${Number(a.amount_nzd || 0).toFixed(2)}`}
        {a.payout_hold && open ? (
          <span className="ml-1.5 rounded-full bg-amber-100 text-amber-800 font-bold px-2 py-0.5">🔒 payout held</span>
        ) : null}
      </div>
      <div className="text-[11px] text-slate-400">
        {(a.reasons || []).join(" · ")} · {a.created_at ? new Date(a.created_at).toLocaleString() : ""}
        {a.refs?.transaction ? ` · ${a.refs.transaction}` : ""}
        {!open
          ? ` · resolved (${a.resolution}) by ${a.resolved_by || "admin"}${a.note ? ` — ${a.note}` : ""}`
          : ""}
      </div>
      {open && (
        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional)"
            maxLength={200}
            data-testid={`risk-note-${a.id}`}
            className="flex-1 min-w-[160px] rounded-full border border-slate-300 px-3 py-1.5 text-xs focus:outline-none focus:border-slate-500"
          />
          <button
            type="button"
            data-testid={`risk-clear-${a.id}`}
            disabled={resolveMut.isPending}
            onClick={() => resolveMut.mutate("clear")}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 py-1.5 disabled:opacity-50 transition-colors"
          >
            Clear &amp; release
          </button>
          <button
            type="button"
            data-testid={`risk-refunded-${a.id}`}
            disabled={resolveMut.isPending}
            onClick={() => resolveMut.mutate("refunded")}
            className="rounded-full border border-rose-300 text-rose-700 text-xs font-bold px-4 py-1.5 hover:bg-rose-50 disabled:opacity-50 transition-colors"
          >
            Refunded
          </button>
        </div>
      )}
    </li>
  );
}

function RiskAlertsCard() {
  const [status, setStatus] = useState("open");
  const q = useQuery({
    queryKey: ["admin-risk-alerts", status],
    retry: false,
    queryFn: async () => (await sharedApi.get(`/admin/engagement/risk-alerts?status=${status}`)).data,
  });
  if (!q.data) return null;
  const alerts = q.data.alerts || [];
  return (
    <div className="rounded-2xl bg-white shadow-card p-5 md:p-6" data-testid="risk-alerts">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-display text-lg font-black text-slate-900">🚨 Risk alerts</h2>
        <span className="text-xs text-slate-500">
          {q.data.open_count || 0} open · {q.data.held_transactions || 0} payout{(q.data.held_transactions || 0) === 1 ? "" : "s"} held
        </span>
        <div className="ml-auto flex gap-1">
          {["open", "all"].map((k) => (
            <button
              key={k}
              type="button"
              data-testid={`risk-filter-${k}`}
              onClick={() => setStatus(k)}
              className={`rounded-full text-[11px] font-bold px-3 py-1 border transition-colors ${
                status === k ? "bg-slate-900 border-slate-900 text-white" : "border-slate-300 text-slate-600 hover:border-slate-500"
              }`}
            >
              {k === "open" ? "Open" : "All"}
            </button>
          ))}
        </div>
      </div>
      {alerts.length === 0 ? (
        <p className="mt-2 text-xs text-slate-500">
          {status === "open" ? "No open alerts — all clear." : "No alerts yet."}
        </p>
      ) : (
        <ul className="mt-2">
          {alerts.map((a) => (
            <RiskAlertRow key={a.id} a={a} />
          ))}
        </ul>
      )}
    </div>
  );
}

/** Iter 636 — monthly $0.20 platform-fee split (vendor vs customer payer).
 *  GET /admin/engagement/fee-report?months=6 — owner/manager/finance only;
 *  hidden on 403 (and while unavailable on older backends). */
function FeeReportCard() {
  const q = useQuery({
    queryKey: ["admin-fee-report"],
    retry: false,
    queryFn: async () => (await sharedApi.get("/admin/engagement/fee-report?months=6")).data,
  });
  if (!q.data) return null;
  const t = q.data.totals || {};
  const rows = [...(q.data.months || []), { ...t, month: "Total", _total: true }];
  return (
    <div className="rounded-2xl bg-white shadow-card p-5 md:p-6" data-testid="fee-report">
      <h2 className="font-display text-lg font-black text-slate-900">
        🏷️ Platform fee report — last {q.data.window_months} months
      </h2>
      <p className="mt-1 text-xs text-slate-500">
        {nzd(q.data.platform_fee_nzd)} per paid visit — the vendor pays only when the customer
        posts a real review; skipped and points-only visits are paid by the customer.
      </p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-slate-400 border-b border-slate-100">
              <th className="py-1.5 pr-3 font-bold">Month</th>
              <th className="py-1.5 pr-3 font-bold">Paid tx</th>
              <th className="py-1.5 pr-3 font-bold">Vendor fees</th>
              <th className="py-1.5 pr-3 font-bold">Customer fees</th>
              <th className="py-1.5 pr-3 font-bold">Refunded as pts</th>
              <th className="py-1.5 pr-3 font-bold">Points settlement</th>
              <th className="py-1.5 font-bold">Net fee revenue</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr
                key={m.month}
                data-testid={m._total ? "fee-report-total" : `fee-report-month-${m.month}`}
                className={m._total ? "text-slate-900 font-black" : "border-b border-slate-50 text-slate-700"}
              >
                <td className="py-1.5 pr-3 font-bold">{m.month}</td>
                <td className="py-1.5 pr-3">{m.paid_tx ?? 0}</td>
                <td className="py-1.5 pr-3">
                  {nzd(m.fee_vendor_nzd)}
                  <span className="text-slate-400"> ({m.fee_vendor_tx ?? 0})</span>
                </td>
                <td className="py-1.5 pr-3">
                  {nzd(m.fee_customer_nzd)}
                  <span className="text-slate-400">
                    {" "}
                    ({m.fee_customer_skipped_tx ?? 0} skipped / {m.fee_customer_points_only_tx ?? 0} points-only)
                  </span>
                </td>
                <td className="py-1.5 pr-3">
                  {nzd(m.refunded_as_points_nzd)}
                  {m.refunded_tx ? <span className="text-slate-400"> ({m.refunded_tx})</span> : null}
                </td>
                <td className="py-1.5 pr-3">{nzd(m.points_settlement_nzd)}</td>
                <td
                  className={`py-1.5 font-bold ${
                    Number(m.net_fee_revenue_nzd) >= 0 ? "text-emerald-700" : "text-rose-700"
                  }`}
                >
                  {nzd(m.net_fee_revenue_nzd)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Iter 630/631 — grant gift/bonus points + recent grants with 24h undo. */
function GrantCard() {
  const qc = useQueryClient();
  const [g, setG] = useState({ user: "", points: "200", kind: "gift", reason: "", message: "" });
  const [undoId, setUndoId] = useState(null);
  const [undoReason, setUndoReason] = useState("");
  const grantsQ = useQuery({
    queryKey: ["admin-grants"],
    retry: false,
    queryFn: async () => (await sharedApi.get("/points/admin/grants?limit=30")).data,
  });
  const grantMut = useMutation({
    mutationFn: async () =>
      (
        await sharedApi.post("/points/admin/grant", {
          user: g.user.trim(),
          points: Number(g.points) || 0,
          kind: g.kind,
          reason: g.reason.trim(),
          message: g.message.trim() || undefined,
        })
      ).data,
    onSuccess: (r) => {
      toast.success(`✅ ${r.points} ${r.kind} pts → ${r.display_name} (balance ${r.new_nz_balance})`);
      setG((x) => ({ ...x, user: "", reason: "", message: "" }));
      qc.invalidateQueries({ queryKey: ["admin-grants"] });
      qc.invalidateQueries({ queryKey: ["admin-grants-report"] });
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Grant failed");
    },
  });
  const undoMut = useMutation({
    mutationFn: async (id) =>
      (await sharedApi.post(`/points/admin/grants/${id}/reverse`, { reason: undoReason.trim() })).data,
    onSuccess: (r) => {
      toast.success(`↩️ ${r.clawed_back} pts taken back · balance ${r.new_nz_balance}`);
      setUndoId(null);
      setUndoReason("");
      qc.invalidateQueries({ queryKey: ["admin-grants"] });
      qc.invalidateQueries({ queryKey: ["admin-grants-report"] });
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Undo failed");
    },
  });
  if (grantsQ.isError && [401, 403].includes(grantsQ.error?.response?.status)) return null;
  const grants = grantsQ.data?.items || [];
  const submit = () => {
    if (!g.user.trim() || !g.reason.trim()) {
      toast.info("Customer and reason are required");
      return;
    }
    const pts = Number(g.points) || 0;
    if (pts < 50 || pts > 50000) {
      toast.info("Points must be between 50 and 50,000");
      return;
    }
    grantMut.mutate();
  };
  const within24h = (r) => Date.now() - new Date(r.created_at).getTime() < 24 * 3600 * 1000;
  const inputCls =
    "w-full rounded-full border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:border-slate-500";
  return (
    <div className="rounded-2xl bg-white shadow-card p-5 md:p-6" data-testid="apx-grant">
      <h2 className="font-display text-lg font-black text-slate-900">🎁 Grant points to a customer</h2>
      <p className="mt-1 text-xs text-slate-500">
        Goodwill for an issue, or a promo gift. Gift = 3-year points usable without a review;
        Bonus = 12-month earned-style points.
      </p>
      <div className="mt-3 space-y-2">
        <input className={inputCls} placeholder="Customer mobile, email or user id" value={g.user}
          onChange={(e) => setG((x) => ({ ...x, user: e.target.value }))} data-testid="apx-grant-user" />
        <div className="flex gap-2">
          <input className={`${inputCls} !w-28`} inputMode="numeric" placeholder="Points" value={g.points}
            onChange={(e) => setG((x) => ({ ...x, points: e.target.value.replace(/[^0-9]/g, "") }))}
            data-testid="apx-grant-points" />
          {["gift", "bonus"].map((k) => (
            <button key={k} type="button" data-testid={`apx-grant-kind-${k}`}
              onClick={() => setG((x) => ({ ...x, kind: k }))}
              className={`flex-1 min-h-[42px] rounded-full border text-sm font-extrabold transition-colors ${
                g.kind === k ? "bg-[#0F70A8] border-[#0F70A8] text-white" : "border-slate-300 text-slate-700 hover:border-slate-500"
              }`}>
              {k === "gift" ? "🎁 Gift" : "⭐ Bonus"}
            </button>
          ))}
        </div>
        <input className={inputCls} placeholder="Internal reason (required, audited)" value={g.reason}
          onChange={(e) => setG((x) => ({ ...x, reason: e.target.value }))} data-testid="apx-grant-reason" />
        <input className={inputCls} placeholder="Message the customer sees (optional)" value={g.message} maxLength={140}
          onChange={(e) => setG((x) => ({ ...x, message: e.target.value.slice(0, 140) }))} data-testid="apx-grant-message" />
        <button type="button" data-testid="apx-grant-submit" disabled={grantMut.isPending} onClick={submit}
          className="w-full min-h-[44px] rounded-full bg-[#0F70A8] hover:bg-[#0c5f8f] text-white text-sm font-black disabled:opacity-60 transition-colors">
          {grantMut.isPending ? <Loader2 className="w-4 h-4 animate-spin inline" /> : `Give ${g.points || 0} ${g.kind} points`}
        </button>
      </div>
      {grants.length > 0 && (
        <div className="mt-4" data-testid="apx-grants">
          <div className="text-xs font-extrabold text-slate-900">Recent grants</div>
          <ul>
            {grants.map((r) => (
              <li key={r.id} className="py-2 border-b border-slate-100 last:border-0" data-testid={`apx-grant-row-${r.id}`}>
                <div className={`text-xs text-slate-600 ${r.reversed_at ? "line-through opacity-60" : ""}`}>
                  {r.kind === "gift" ? "🎁" : "⭐"}{" "}
                  <span className="font-extrabold text-slate-900">+{Number(r.points).toLocaleString()} pts</span> →{" "}
                  {r.user_id} · {r.reason}
                </div>
                <div className={`text-[11px] text-slate-400 ${r.reversed_at ? "line-through opacity-60" : ""}`}>
                  {new Date(r.created_at).toLocaleString()} · by {r.admin_email || r.admin_role || "admin"}
                  {r.expires_at ? ` · expires ${new Date(r.expires_at).toLocaleDateString()}` : ""}
                </div>
                {r.reversed_at ? (
                  <div className="text-[11px] font-bold text-rose-600">
                    ↩️ Reversed {new Date(r.reversed_at).toLocaleString()} by {r.reversed_by_email || "admin"} ·{" "}
                    {r.reversal_reason || ""} · clawed back {r.clawed_back}
                    {r.already_spent ? ` (${r.already_spent} already spent)` : ""}
                  </div>
                ) : within24h(r) ? (
                  undoId === r.id ? (
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                      <input className="flex-1 min-w-[180px] rounded-full border border-slate-300 px-3 py-1.5 text-xs focus:outline-none focus:border-slate-500"
                        placeholder="Undo reason (required)" value={undoReason}
                        onChange={(e) => setUndoReason(e.target.value)} data-testid={`apx-undo-reason-${r.id}`} />
                      <button type="button" data-testid={`apx-undo-confirm-${r.id}`}
                        disabled={undoMut.isPending || !undoReason.trim()} onClick={() => undoMut.mutate(r.id)}
                        className="rounded-full bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold px-4 py-1.5 disabled:opacity-50 transition-colors">
                        Confirm undo
                      </button>
                      <button type="button" data-testid={`apx-undo-cancel-${r.id}`}
                        onClick={() => { setUndoId(null); setUndoReason(""); }}
                        className="text-xs font-bold text-slate-500 underline">
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button type="button" data-testid={`apx-undo-${r.id}`} onClick={() => setUndoId(r.id)}
                      className="mt-1 text-[11px] font-bold text-rose-600 underline">
                      ↩️ Undo (within 24 h)
                    </button>
                  )
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Iter 631 §3 — monthly goodwill report; hidden on 403. */
function GrantsReportCard() {
  const q = useQuery({
    queryKey: ["admin-grants-report"],
    retry: false,
    queryFn: async () => (await sharedApi.get("/points/admin/grants/report?months=6")).data,
  });
  if (!q.data) return null;
  const t = q.data.totals || {};
  return (
    <div className="rounded-2xl bg-white shadow-card p-5 md:p-6" data-testid="apx-report">
      <h2 className="font-display text-lg font-black text-slate-900">📈 Goodwill report — last {q.data.window_months} months</h2>
      <p className="mt-1 text-xs text-slate-500">
        {t.grants || 0} grants · {Number(t.points || 0).toLocaleString()} pts ({nzd(t.value_nzd)}) ·{" "}
        {t.reversed || 0} reversed ({Number(t.clawed_back || 0).toLocaleString()} pts clawed back) · 🎁{" "}
        {t.by_kind?.gift || 0} / ⭐ {t.by_kind?.bonus || 0}
      </p>
      <ul className="mt-2">
        {(q.data.months || []).map((m) => (
          <li key={m.month} className="py-2 border-t border-slate-100 text-xs text-slate-600" data-testid={`apx-report-month-${m.month}`}>
            <span className="font-extrabold text-slate-900">{m.month}</span> — {m.grants} grants ·{" "}
            {Number(m.points).toLocaleString()} pts ({nzd(m.value_nzd)})
            {m.reversed ? ` · ${m.reversed} reversed` : ""}
            {m.by_reason?.length ? (
              <span className="block text-[11px] text-slate-400">
                Top reasons: {m.by_reason.slice(0, 3).map((r) => `${r.reason} (${r.grants})`).join(" · ")}
              </span>
            ) : null}
            {m.by_admin?.length ? (
              <span className="block text-[11px] text-slate-400">
                By admin: {m.by_admin.slice(0, 3).map((a) => `${a.admin} (${a.grants})`).join(" · ")}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PnlBlock() {
  const q = useQuery({
    queryKey: ["admin-points-pnl"],
    retry: false,
    queryFn: async () =>
      (await sharedApi.get("/points/admin/purchased-points/pnl?months=12")).data,
  });
  const d = q.data;
  if (!d) return null;
  const t = d.totals || {};
  return (
    <div className="rounded-2xl bg-white shadow-card p-5 md:p-6" data-testid="apx-pnl">
      <h2 className="font-display text-lg font-black text-slate-900">P&amp;L — last 12 months</h2>
      <p className="mt-1 text-xs text-slate-500">
        Outstanding: {Number(d.outstanding_purchased_points || 0).toLocaleString()} pts (
        {nzd(d.outstanding_liability_nzd)} liability) · settlement fee {d.settlement_pct}%
      </p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-slate-400 border-b border-slate-100">
              <th className="py-1.5 pr-3 font-bold">Month</th>
              <th className="py-1.5 pr-3 font-bold">Buys</th>
              <th className="py-1.5 pr-3 font-bold">Cash in</th>
              <th className="py-1.5 pr-3 font-bold">Fees</th>
              <th className="py-1.5 pr-3 font-bold">Stripe est.</th>
              <th className="py-1.5 pr-3 font-bold">Refunded</th>
              <th className="py-1.5 pr-3 font-bold">Redeemed</th>
              <th className="py-1.5 pr-3 font-bold">Settlement</th>
              <th className="py-1.5 font-bold">Margin</th>
            </tr>
          </thead>
          <tbody>
            {(d.months || []).map((m) => (
              <tr key={m.month} className="border-b border-slate-50 text-slate-700">
                <td className="py-1.5 pr-3 font-bold">{m.month}</td>
                <td className="py-1.5 pr-3">{m.purchases}</td>
                <td className="py-1.5 pr-3">{nzd(m.cash_in_nzd)}</td>
                <td className="py-1.5 pr-3">{nzd(m.fee_revenue_nzd)}</td>
                <td className="py-1.5 pr-3">{nzd(m.stripe_cost_est_nzd)}</td>
                <td className="py-1.5 pr-3">{nzd(m.refunded_nzd)}</td>
                <td className="py-1.5 pr-3">{nzd(m.redeemed_nzd)}</td>
                <td className="py-1.5 pr-3">{nzd(m.settlement_fee_nzd)}</td>
                <td className={`py-1.5 font-bold ${Number(m.realised_margin_nzd) >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                  {nzd(m.realised_margin_nzd)}
                </td>
              </tr>
            ))}
            {t && (
              <tr className="text-slate-900 font-black">
                <td className="py-2 pr-3">Total</td>
                <td className="py-2 pr-3">{t.purchases ?? ""}</td>
                <td className="py-2 pr-3">{nzd(t.cash_in_nzd)}</td>
                <td className="py-2 pr-3">{nzd(t.fee_revenue_nzd)}</td>
                <td className="py-2 pr-3">{nzd(t.stripe_cost_est_nzd)}</td>
                <td className="py-2 pr-3">{nzd(t.refunded_nzd)}</td>
                <td className="py-2 pr-3">{nzd(t.redeemed_nzd)}</td>
                <td className="py-2 pr-3">{nzd(t.settlement_fee_nzd)}</td>
                <td className="py-2">{nzd(t.realised_margin_nzd)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PurchaseRow({ p }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const refundMut = useMutation({
    mutationFn: async () =>
      (
        await sharedApi.post(`/points/purchase/admin/${p.id}/refund`, {
          reason: reason.trim(),
          refund_stripe: true,
        })
      ).data,
    onSuccess: () => {
      toast.success("Refunded — points reversed and Stripe refund issued.");
      setConfirming(false);
      qc.invalidateQueries({ queryKey: ["admin-points-purchases"] });
      qc.invalidateQueries({ queryKey: ["admin-points-pnl"] });
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Refund failed.");
    },
  });
  const refundable = p.status === "paid" && !p.refunded_at;
  return (
    <li className="py-3 border-b border-slate-100 last:border-0" data-testid={`apx-row-${p.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold text-slate-900">
            {p.is_gift ? "🎁 Gift" : "➕ Self"} · {Number(p.points || 0).toLocaleString()} pts ·{" "}
            {nzd(p.total_nzd)}
          </div>
          <div className="text-xs text-slate-500">
            {p.id} · {p.buyer_display || p.buyer_email || "buyer"}
            {p.is_gift ? ` → ${p.recipient_display || "recipient"}` : ""} ·{" "}
            {p.created_at ? new Date(p.created_at).toLocaleString() : ""} ·{" "}
            <span className={p.status === "paid" ? "text-emerald-700 font-bold" : "font-bold"}>
              {p.refunded_at ? "refunded" : p.status}
            </span>
          </div>
        </div>
        {refundable && !confirming && (
          <button
            type="button"
            data-testid={`apx-refund-${p.id}`}
            onClick={() => setConfirming(true)}
            className="rounded-full border border-rose-300 text-rose-700 text-xs font-bold px-4 py-1.5 hover:bg-rose-50 transition-colors"
          >
            Refund…
          </button>
        )}
      </div>
      {confirming && (
        <div className="mt-2 rounded-xl bg-rose-50 border border-rose-200 p-3">
          <p className="text-xs font-bold text-rose-800">
            Reverse the points and refund {nzd(p.total_nzd)} on Stripe?
          </p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (required)"
              maxLength={200}
              data-testid={`apx-reason-${p.id}`}
              className="flex-1 min-w-[200px] rounded-full border border-slate-300 px-3 py-1.5 text-xs focus:outline-none focus:border-slate-500"
            />
            <button
              type="button"
              data-testid={`apx-refund-confirm-${p.id}`}
              disabled={refundMut.isPending || !reason.trim()}
              onClick={() => refundMut.mutate()}
              className="rounded-full bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold px-4 py-1.5 disabled:opacity-50 transition-colors"
            >
              {refundMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Confirm refund"}
            </button>
            <button
              type="button"
              data-testid={`apx-refund-cancel-${p.id}`}
              onClick={() => setConfirming(false)}
              className="text-xs font-bold text-slate-500 underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

export default function AdminPointsPurchasesPage() {
  const listQ = useQuery({
    queryKey: ["admin-points-purchases"],
    retry: false,
    queryFn: async () =>
      (await sharedApi.get("/points/purchase/admin/list?limit=200")).data,
  });
  // Iter 630 §3 — a sub-admin holding ONLY the "points_grant" module can't
  // read the P&L/purchases (403). Keep the grant card usable and just hide
  // what they can't see — never an error toast. Same queryKey as GrantCard
  // so react-query dedupes into a single request.
  const grantsQ = useQuery({
    queryKey: ["admin-grants"],
    retry: false,
    queryFn: async () => (await sharedApi.get("/points/admin/grants?limit=30")).data,
  });

  const listDenied = [401, 403].includes(listQ.error?.response?.status);
  const grantsDenied = grantsQ.isError;
  const denied = listDenied && grantsDenied;
  const loading = listQ.isLoading || grantsQ.isLoading;
  const items = listQ.data?.items || [];

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title="Purchased points — admin" description="Purchased points sales, P&L and refunds." />
      <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">
        💳 Purchased points — sales, P&amp;L, refunds, grant points
      </h1>
      {denied ? (
        <div className="mt-6 rounded-2xl bg-slate-50 border border-slate-200 p-8 text-center" data-testid="apx-denied">
          <ShieldAlert className="w-7 h-7 mx-auto text-slate-400" />
          <p className="mt-2 text-sm font-bold text-slate-700">Not authorised</p>
          <p className="mt-1 text-xs text-slate-500">This area is for AllSale staff with the rewards or finance role.</p>
        </div>
      ) : loading ? (
        <div className="mt-6 grid place-items-center rounded-2xl bg-slate-50 border border-slate-200 p-10">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="mt-6 space-y-5">
          <RiskAlertsCard />
          <FeeReportCard />
          {!listDenied && <PnlBlock />}
          <GrantCard />
          <GrantsReportCard />
          {!listDenied && (
            <div className="rounded-2xl bg-white shadow-card p-5 md:p-6">
              <h2 className="font-display text-lg font-black text-slate-900">Purchases &amp; gifts</h2>
              {items.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">No purchases yet.</p>
              ) : (
                <ul className="mt-1">
                  {items.map((p) => (
                    <PurchaseRow key={p.id} p={p} />
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
